import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class PasswordGeneratorService {
  private readonly lowercase = 'abcdefghijklmnopqrstuvwxyz';
  private readonly uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  private readonly numbers = '0123456789';
  // Use only common safe special characters that are widely accepted
  private readonly specialChars = '!@#$%^&*';

  /**
   * Generates a random password that meets all requirements:
   * - At least 8 characters (default: 12)
   * - At least 1 lowercase letter (a-z)
   * - At least 1 uppercase letter (A-Z)
   * - At least 1 number (0-9)
   * - At least 1 special character
   */
  generatePassword(length: number = 12): string {
    if (length < 8) {
      length = 8;
    }

    const allChars =
      this.lowercase + this.uppercase + this.numbers + this.specialChars;

    // Ensure at least one of each required character type
    const requiredChars = [
      this.getRandomChar(this.lowercase),
      this.getRandomChar(this.uppercase),
      this.getRandomChar(this.numbers),
      this.getRandomChar(this.specialChars)
    ];

    // Fill remaining length with random characters from all types
    const remainingLength = length - requiredChars.length;
    const randomChars: string[] = [];

    for (let i = 0; i < remainingLength; i++) {
      randomChars.push(this.getRandomChar(allChars));
    }

    // Combine and shuffle all characters
    const allPasswordChars = [...requiredChars, ...randomChars];
    return this.shuffleArray(allPasswordChars).join('');
  }

  /**
   * Validates if a password meets all requirements
   */
  isValidPassword(password: string): boolean {
    if (!password || password.length < 8) {
      return false;
    }

    const hasLowercase = /[a-z]/.test(password);
    const hasUppercase = /[A-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecial = /[!@#$%^&*]/.test(password);

    return hasLowercase && hasUppercase && hasNumber && hasSpecial;
  }

  private getRandomChar(chars: string): string {
    const array = new Uint32Array(1);
    crypto.getRandomValues(array);
    const randomIndex = array[0] % chars.length;
    return chars[randomIndex];
  }

  private shuffleArray<T>(array: T[]): T[] {
    const shuffled = [...array];
    const randomArray = new Uint32Array(shuffled.length);
    crypto.getRandomValues(randomArray);
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = randomArray[i] % (i + 1);
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }
}
